import React, { useEffect, useState, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { RootState } from "../../redux/store";
import { Box } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { addItem } from "../../redux/features/slices/cartSlice";
import emptyCartIcon from "../../assets/emptycartIcon.svg";
import { CustomerEndPoints, productEndPoints } from "../../constants/endpoints";
import { api } from "../../api/api";
import {
  blockInventoryService,
  unblockInventoryService,
} from "../../service/cartService";
import { cartDataMapper } from "../../utils/cartDataMapper";
import { useCart, useUpdateCart } from "../../queries/cart";
import { useQueryClient } from "@tanstack/react-query";
import useIsMobile from "../../common/mobileVersion/useIsMobile";
import { v4 as uuidv4 } from "uuid";
import { createOrder } from "../../api/orderFinalAPI";
import { getUserEmailFromLocalStorage } from "../../utils/localStorageUtils";
import { fetchProductData } from "../../api/productDataAPI";
import { mapProduct } from "../../utils/productListMapping";
import CartItem from "./CartItem";
import OrderSummary from "./OrderSummary";
import PageHeader from "../../common/PageHeader";
import PaymentDialog from "../../common/paymentDialog";
import SuccessIcon from "../../assets/icons/payment-success-popup-icon.svg";
import {
  CartContainer,
  CartLayout,
  CartItemsWrapper,
  CartItemsContainer,
  OrderSummaryWrapper,
  OrderSummaryContainer,
  EmptyCartContainer,
  EmptyCartImage,
  EmptyCartText,
} from "./Cart.styles";
import Toast from "../../common/Toast";
import { ExploreButton } from "../wishlist/Wishlist.Styles";

interface CartItem {
  id: number;
  name: string;
  price: number;
  quantity: number;
  image: string;
  unit: number;
}

const Cart: React.FC = () => {
  const [selectedSubTotalItems, setSelectedSubTotalItems] = useState<string[]>(
    []
  );
  const [productInventories, setProductInventories] = useState<
    Record<string, any>
  >({});
  const [selectedItems, setSelectedItems] = useState<number>(0);
  const [subTotal, setSubTotal] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState(false); // Modal state
  const items = useAppSelector((state: RootState) => state.cart.items);
  const dispatch = useAppDispatch();
  const [error, setError] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isPaymentSuccessful, setIsPaymentSuccessful] = useState(false);
  const [orderPayload, setOrderPayload] = useState<any>({});
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");
  const isMobile = useIsMobile();
  const firstRender = useRef(true);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState<string | null>(null);
  const { data: cartData } = useCart();
  const updateCart = useUpdateCart();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (cartData?.Products) saveItemsToStore(cartData);
  }, [cartData]);

  const [note, setNote] = useState("");
  const navigate = useNavigate();
  const orgId = localStorage.getItem("orgId");
  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);
  useEffect(() => {
    const fetchProductInventories = async () => {
      try {
        const cartItems = cartData?.Products || [];
        if (!cartItems.length) return;
        const productIds = cartItems.map((item: any) => item.productId);
        const inventoryMap: Record<string, any> = {};
        for (const id of productIds) {
          const productData = await fetchProductData(
            id,
            productEndPoints.getProducts
          );
          const mapped = mapProduct(productData);
          inventoryMap[id] = mapped;
        }
        setProductInventories(inventoryMap);
      } catch (error) {
        console.error("Failed to fetch inventories", error);
      }
    };
    if (cartData?.Products) fetchProductInventories();
  }, [cartData]);

  useEffect(() => {
    //remove local storage
    localStorage.removeItem("tempPayload");

    //chnange first render to false
    if (firstRender.current) {
      firstRender.current = false;
    }

    if (items?.length) {
      const initialSelectedIds = items.map(
        (item) => `${item.id}_${item.quantity}`
      );
      setSelectedSubTotalItems(initialSelectedIds);
      setSelectedItems(initialSelectedIds.length);
    }

    if (orderId) setIsModalOpen(true);

    return () => {
      const unblockInventoryOnComponentUnmount = async () => {
        try {
          const tempPayload = localStorage.getItem("tempPayload");
          if (
            !isPaymentSuccessful &&
            !firstRender.current &&
            tempPayload &&
            Object.keys(JSON.parse(tempPayload)).length
          ) {
            console.log("Payment not successful, unblocking inventory...");
            await unblockInventoryService(JSON.parse(tempPayload));
            console.log("Inventory unblocked successfully.");
          }
        } catch (error: any) {
          console.log("something went wrong while unblocking inventory", error);
        }
      };

      unblockInventoryOnComponentUnmount();
    };
  }, [isPaymentSuccessful]);

  const saveItemsToStore = (data: any) => {
    if (!data?.Products) {
      console.error("No cart data received");
      return;
    }
    setSubTotal(+data?.subTotal);
    const newItems: CartItem[] = cartDataMapper(data.Products);
    if (itemsRef.current.length > 0) {
      const currentKeys = itemsRef.current.map(
        (item: CartItem) => `${item.id}_${item.quantity}`
      );
      const newItemsMap = new Map<string, CartItem>();
      newItems.forEach((item: CartItem) => {
        newItemsMap.set(`${item.id}_${item.quantity}`, item);
      });
      const orderedNewItems: CartItem[] = [];
      currentKeys.forEach((key: string) => {
        const item = newItemsMap.get(key);
        if (item) orderedNewItems.push(item);
      });
      newItems.forEach((item: CartItem) => {
        const key = `${item.id}_${item.quantity}`;
        if (!currentKeys.includes(key)) {
          orderedNewItems.push(item);
        }
      });
      dispatch(addItem(orderedNewItems));
    } else {
      dispatch(addItem(newItems));
    }
  };

  const incrementQuantity = async (item: CartItem) => {
    try {
      const inventoryItem = productInventories[item.id];
      if (!inventoryItem) {
        setToastMessage("Inventory data not available for this product");
        setError(true);
        setShowToast(true);
        return;
      }

      const unitKey = `availableQuantity${item.quantity}L`;
      const availableQuantity = inventoryItem[unitKey];
      if (!availableQuantity || item.unit + 1 > availableQuantity) {
        setToastMessage("Cannot add more than available inventory");
        setError(true);
        setShowToast(true);
        return;
      }
      const updateCartData = {
        productId: item.id,
        unit: +item.unit > 0 ? item.unit + 1 : item.unit,
        quantity: +item.quantity,
      };
      const updatedCart = await updateCart.mutateAsync({
        cartData: [updateCartData],
      });
      saveItemsToStore(updatedCart);
      setError(false);
      setToastMessage("Cart updated successfully");
      setShowToast(true);
    } catch (error: any) {
      setToastMessage("Failed to Increment Cart details");
      setError(true);
      setShowToast(true);
      console.log("Error in Updating", error.message);
    }
  };

  const decrementQuantity = async (item: CartItem) => {
    try {
      if (item.unit > 1) {
        const updateCartData = {
          productId: item.id,
          unit: +item.unit > 0 ? item.unit - 1 : item.unit,
          quantity: +item.quantity,
        };
        const updatedCart = await updateCart.mutateAsync({
          cartData: [updateCartData],
        });
        saveItemsToStore(updatedCart);
        setError(false);
        setToastMessage("Cart updated successfully");
        setShowToast(true);
      } else {
        await removeItem(item);
      }
    } catch (error: any) {
      setToastMessage("Failed to Decrement Cart details");
      setError(true);
      setShowToast(true);
      console.log("Error in Updating", error.message);
    }
  };

  const removeItem = async (item: CartItem) => {
    try {
      const removeItemData = {
        productId: item.id,
        quantity: item.quantity,
        unit: 0,
      };
      const updatedCart = await updateCart.mutateAsync({
        cartData: [removeItemData],
      });
      saveItemsToStore(updatedCart);
      if (selectedSubTotalItems.includes(`${item.id}_${item.quantity}`)) {
        setSelectedItems(selectedItems - 1);
      }
      setToastMessage("Product removed from cart");
      setError(false);
      setShowToast(true);
    } catch (error: any) {
      setToastMessage("Failed to Delete Cart Item");
      setError(true);
      setShowToast(true);
      console.log("Error in Updating", error.message);
    }
  };

  const emptyCart = async (removeList: string[]) => {
    try {
      const removeItemData = removeList.map((item) => {
        const [id, quantity] = item.split("_");
        return {
          productId: id,
          quantity: Number(quantity),
        };
      });

      const updatedCart = await updateCart.mutateAsync({
        deletedProductsFromCart: removeItemData,
      });
      saveItemsToStore(updatedCart);
    } catch (error: any) {
      setToastMessage("Failed to Delete Cart Item");
      setError(true);
      setShowToast(true);
      console.log("Error in Updating", error.message);
    }
  };

  const getFormattedQuantity = (value: number, quantity: number): string => {
    if (!value || typeof value !== "number") {
      console.error("Invalid or empty value:", value);
      return "Invalid quantity";
    }

    const numericValue = value;
    if (isNaN(numericValue)) {
      return `${value}`;
    }

    const multiplier = Math.floor(numericValue);
    const totalLiters = multiplier * quantity;
    if (isMobile) {
      return `${totalLiters} Litres`;
    }
    if (multiplier === 1) {
      return `${numericValue} Litres`;
    }

    return `${multiplier} x ${quantity} = ${totalLiters} L`;
  };

  const selectedTotal =
    items && items.length
      ? items.reduce(
          (acc, item) =>
            selectedSubTotalItems.includes(`${item.id}_${item.quantity}`)
              ? {
                  count: acc.count + item.quantity,
                  price: acc.price + item.price * item.quantity * item.unit,
                }
              : acc,
          { count: 0, price: 0 }
        )
      : { count: 0, price: 0 };

  const fetchUserData = async (emailId: string) => {
    try {
      const response = await api.get(
        `${CustomerEndPoints.getCustomerByEmail}/${emailId}`,
        {
          params: {
            orgId: localStorage.getItem("organizationId"),
          },
          headers: {
            orgId: localStorage.getItem("organizationId"),
          },
        }
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching user data:", error);
      throw error;
    }
  };

  const handlePlaceOrder = async () => {
    try {
      const emailId = getUserEmailFromLocalStorage() || "";
      console.log("Email retrieved from userRole key:", emailId);

      if (!emailId) {
        console.warn("No email ID found in userRole key.");
      }

      const customerData = await fetchUserData(emailId);

      const filteredProducts = items.filter((item) =>
        selectedSubTotalItems.includes(`${item.id}_${item.quantity}`)
      );

      const orderDetails = filteredProducts.map((item) => ({
        id: uuidv4(),
        orderQuantity: item.quantity,
        unit: item.unit,
        productId: item.id.toString(),
      }));

      const tempOrderPayload = {
        orderStatus: "NEW",
        paymentStatus: "PENDING",
        totalAmountInCents: selectedTotal.price * 100,
        customerId: customerData?.customer?.id,
        taxPercentage: 10,
        discountPercentage: 5,
        billingAddress: customerData.address?.registeredAddress,
        shippingAddress: customerData.address?.licenseAddress,
        orderDetails,
        customerNote: note?.trim() || null,
      };

      localStorage.setItem("tempPayload", JSON.stringify(tempOrderPayload));

      setOrderPayload(tempOrderPayload);
      if (!(orderDetails && orderDetails.length)) {
        setToastMessage("No Items Selected For Checkout");
        setShowToast(true);
        setError(true);
        return;
      }
      await blockInventoryService({ orderDetails });

      const response: any = await createOrder(tempOrderPayload);
      const parsedResponse = JSON.parse(response.body);
      const order = parsedResponse.data.order;
      localStorage.removeItem("tempPayload");
      await emptyCart(selectedSubTotalItems);
      queryClient.invalidateQueries({ queryKey: ["cart"] });
      setIsPaymentSuccessful(true);
      setCreatedOrderId(order.id);
      setIsPaymentDialogOpen(true);
    } catch (error: any) {
      const backendError = error?.response?.data?.error;
      const backendMessage =
        (typeof backendError?.type === "object"
          ? backendError?.type?.message
          : backendError?.type) ||
        backendError?.message ||
        error?.message;
      setToastMessage(backendMessage || "Something went wrong");
      setShowToast(true);
      setError(true);
      console.error("Error proceeding to buy:", error);
    }
  };

  const getPopupContent = () => {
    return (
      <>
        Thank you for ordering at Maltora. Your order will be processed as soon
        as possible.
        <br />
        Make sure you make note of your order number, which is{" "}
        <span id="OrdId">{createdOrderId ?? ""}</span>. <br />
        You will be receiving an email shortly with an invoice from your order.
      </>
    );
  };

  const handleViewAll = () => {
    navigate(`/maltora-customer/products`);
  };

  return (
    <>
      <CartContainer>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <PageHeader title="Shopping Cart" />
        </Box>
        {Array.isArray(items) && items.length > 0 ? (
          <CartLayout>
            {/* Left Grid: Cart Items */}
            <CartItemsWrapper>
              <CartItemsContainer elevation={0}>
                {items.map((item) => (
                  <CartItem
                    key={item.id}
                    item={item}
                    onIncrement={() => incrementQuantity(item)}
                    onDecrement={() => decrementQuantity(item)}
                    onRemove={() => removeItem(item)}
                    getFormattedQuantity={getFormattedQuantity}
                  />
                ))}
              </CartItemsContainer>
            </CartItemsWrapper>

            {/* Right Grid: Order Summary */}
            <OrderSummaryWrapper>
              <OrderSummaryContainer elevation={0}>
                <OrderSummary
                  onPlaceOrder={handlePlaceOrder}
                  cartItems={items}
                  note={note}
                  onNoteChange={setNote}
                />
              </OrderSummaryContainer>
            </OrderSummaryWrapper>
          </CartLayout>
        ) : (
          <>
            <EmptyCartContainer>
              <EmptyCartImage src={emptyCartIcon} alt="Empty Cart" />

              <EmptyCartText variant="h6">
                Your shopping cart is empty
                <br /> Please explore our brews and add them to your cart
              </EmptyCartText>
              <ExploreButton onClick={handleViewAll}>
                Explore our Brews
              </ExploreButton>
            </EmptyCartContainer>
          </>
        )}
      </CartContainer>

      <PaymentDialog
        open={isPaymentDialogOpen}
        onClose={() => setIsPaymentDialogOpen(false)}
        title="Order Placed Successfully"
        message={getPopupContent()}
        secondaryText="Back to shopping"
        primaryText="Make Payment"
        primaryAction={() => {
          setIsPaymentDialogOpen(false);
          navigate(`/maltora-customer/order-confirmation/${createdOrderId}`);
        }}
        secondaryAction={() => {
          setIsPaymentDialogOpen(false);
          navigate(`/maltora-customer/products`);
        }}
        imageUrl={SuccessIcon}
      />
      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={error}
      />
    </>
  );
};

export default Cart;
